import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms'
import { Injectable } from '@angular/core'

import { withoutGuarantors } from '@data/constants/contact-types'

@Injectable({
  providedIn: 'root',
})
export class CustomerService {
  entity!: FormGroup
  formMethod = 'CREATE_PHYSICAL_PERSON'
  isEditMode = false // Nueva propiedad para detectar modo edición
  //* Sin «Obligado solidario» a propósito: los obligados se capturan en su propia tarjeta del detalle
  //* (`PUT /clients/:id/guarantors`); el `PUT /clients/:id` genérico los ignora (plan v4.3 §9.5).
  contactTypes: { label: string; value: string }[] = [
    { label: 'Administrativo', value: 'Administrativo' },
    { label: 'Accionista', value: 'Accionista' },
    { label: 'Gerente', value: 'Gerente' },
    { label: 'Otro', value: 'Otro' },
  ]

  fiscalRegimes: { label: string; value: string }[] = [
    { label: '601 - General de Ley Personas Morales', value: '601' },
    { label: '603 - Personas Morales con Fines no Lucrativos', value: '603' },
    { label: '605 - Sueldos y Salarios e Ingresos Asimilados a Salarios', value: '605' },
    { label: '606 - Arrendamiento', value: '606' },
    { label: '607 - Régimen de Enajenación o Adquisición de Bienes', value: '607' },
    { label: '608 - Demás Ingresos', value: '608' },
    { label: '610 - Residentes en el Extranjero sin Establecimiento Permanente en México', value: '610' },
    { label: '611 - Ingresos por Dividendos (Socios y Accionistas)', value: '611' },
    { label: '612 - Personas Físicas con Actividades Empresariales y Profesionales', value: '612' },
    { label: '614 - Ingresos por Intereses', value: '614' },
    { label: '615 - Régimen de los Ingresos por Obtención de Premios', value: '615' },
    { label: '616 - Sin Obligaciones Fiscales', value: '616' },
    { label: '620 - Sociedades Cooperativas de Producción que optan por Diferir sus Ingresos', value: '620' },
    { label: '621 - Incorporación Fiscal', value: '621' },
    { label: '622 - Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras', value: '622' },
    { label: '623 - Opcional para Grupos de Sociedades', value: '623' },
    { label: '624 - Coordinados', value: '624' },
    { label: '625 - Régimen de las Actividades Empresariales con Ingresos a través de Plataformas Tecnológicas', value: '625' },
    { label: '626 - Régimen Simplificado de Confianza', value: '626' },
  ]

  constructor(private fb: FormBuilder) {}

  /**
   * Obtiene los validadores apropiados para un campo según el modo (creación/edición)
   */
  private getValidatorsForField(fieldName: string) {
    // Si estamos en modo edición, no aplicar validaciones a estos campos específicos
    if (this.isEditMode) {
      const fieldsWithoutValidationInEdit = ['lastName', 'middleName', 'rfc', 'curp', 'email', 'phone', 'fiscalRegime']
      if (fieldsWithoutValidationInEdit.includes(fieldName)) {
        return [] // Sin validaciones en modo edición
      }
    }

    // Validaciones para modo creación
    switch (fieldName) {
      case 'lastName':
        return [Validators.required, Validators.minLength(3)]
      case 'middleName':
        return [Validators.required, Validators.minLength(3)]
      case 'rfc':
        return [Validators.required, Validators.pattern(/^[A-Z]{3,4}[0-9]{6}[A-Z0-9]{3}$/)]
      case 'curp':
        return [Validators.required, Validators.pattern(/^[A-Z]{4}[0-9]{5,6}[A-Z]{6}[0-9]{2}$/)]
      case 'email':
        return [Validators.required, Validators.email]
      case 'phone':
        return [Validators.required, Validators.pattern(/^\d{10}$/)]
      default:
        return []
    }
  }

  newEntity() {
    // Solo resetear modo edición si no estamos ya en modo edición
    if (!this.isEditMode) this.isEditMode = false

    switch (this.formMethod) {
      case 'CREATE_PHYSICAL_PERSON':
        this.entity = this.fb.group({
          entity: ['Física', [Validators.required]],
          industrySector: ['', [Validators.required]],
          industrySubsector: ['', [Validators.required]],
          name: ['', [Validators.required, Validators.minLength(3)]],
          lastName: ['', this.getValidatorsForField('lastName')],
          middleName: ['', this.getValidatorsForField('middleName')],
          rfc: ['', this.getValidatorsForField('rfc')],
          curp: ['', this.getValidatorsForField('curp')],
          nationality: ['', [Validators.required]],
          fiscalRegime: ['', [Validators.required]],
          email: ['', this.getValidatorsForField('email')],
          phone: ['', this.getValidatorsForField('phone')],
          address: this.fb.group({
            country: ['', [Validators.required]],
            state: ['', [Validators.required]],
            municipality: ['', [Validators.required]],
            colony: ['', [Validators.required]],
            city: ['', [Validators.required]],
            postalCode: ['', [Validators.required, Validators.pattern(/^[0-9]{5}$/)]],
            street: ['', [Validators.required]],
            exteriorNumber: ['', [Validators.pattern(/^[0-9]+[A-Z]?$/)]],
            interiorNumber: ['', [Validators.pattern(/^[0-9]+[A-Z]?$/)]],
          }),
          anualIncome: [0, [Validators.required, Validators.min(1)]],
          employees: [0, [Validators.required, Validators.min(0)]],
          contacts: this.fb.array([this.createContact()]),
          channelKey: [null], //* Omnicanal: canal de origen (opcional, backend auto-resuelve por actor)
        })

        this.handleSameLegalAddress()
        break
      case 'CREATE_MORAL_PERSON':
        this.entity = this.fb.group({
          entity: ['Moral', [Validators.required]],
          industrySector: ['', [Validators.required]],
          industrySubsector: ['', [Validators.required]],
          name: ['', [Validators.required, Validators.minLength(3)]],
          legalName: ['', [Validators.required, Validators.minLength(3)]],
          rfc: ['', this.getValidatorsForField('rfc')],
          fiscalRegime: ['', [Validators.required]],
          email: ['', this.getValidatorsForField('email')],
          phone: ['', this.getValidatorsForField('phone')],
          address: this.fb.group({
            country: ['', [Validators.required]],
            state: ['', [Validators.required]],
            municipality: ['', [Validators.required]],
            colony: ['', [Validators.required]],
            city: ['', [Validators.required]],
            postalCode: ['', [Validators.required, Validators.pattern(/^[0-9]{5}$/)]],
            street: ['', [Validators.required]],
            exteriorNumber: ['', [Validators.pattern(/^[0-9]+[A-Z]?$/)]],
            interiorNumber: ['', [Validators.pattern(/^[0-9]+[A-Z]?$/)]],
          }),
          anualIncome: [0, [Validators.required, Validators.min(1)]],
          employees: [0, [Validators.required, Validators.min(0)]],
          contacts: this.fb.array([this.createContact()]),
          sameLegalAddress: [false, [Validators.required]],
          legalAddress: this.addLegalAddress(),
          channelKey: [null], //* Omnicanal: canal de origen (opcional, backend auto-resuelve por actor)
        })
        this.handleSameLegalAddress()
        break
    }
  }

  handleSameLegalAddress() {
    this.entity.get('sameLegalAddress')?.valueChanges.subscribe((sameLegalAddress) => {
      const legalAddress = this.entity.get('legalAddress') as FormGroup
      if (sameLegalAddress || this.formMethod === 'CREATE_PHYSICAL_PERSON') {
        // Si sameLegalAddress es true, desactivamos las validaciones de legalAddress
        Object.keys(legalAddress.controls).forEach((key) => {
          legalAddress.get(key)?.clearValidators()
          legalAddress.get(key)?.updateValueAndValidity()
        })
      } else {
        // Si sameLegalAddress es false, activamos las validaciones de legalAddress
        Object.keys(legalAddress.controls).forEach((key) => {
          legalAddress.get(key)?.setValidators([Validators.required])
          legalAddress.get(key)?.updateValueAndValidity()
        })
      }
    })
  }

  createContact(): FormGroup {
    return this.fb.group({
      contactType: ['', [Validators.required]],
      email: ['', this.getValidatorsForField('email')],
      phone: ['', this.getValidatorsForField('phone')],
      firstName: ['', [Validators.required, Validators.minLength(3)]],
      lastName: ['', [Validators.minLength(3)]],
      middleName: ['', [Validators.minLength(3)]],
      rfc: [null, this.isEditMode ? [] : [Validators.pattern(/^[A-Z]{3,4}[0-9]{6}[A-Z0-9]{3}$/)]],
      curp: [null, this.isEditMode ? [] : [Validators.pattern(/^[A-Z]{4}[0-9]{6}[A-Z]{5,6}[0-9]{2}$/)]],
      position: ['', [Validators.required, Validators.minLength(3)]],
      participationPercentage: [0, [Validators.required, Validators.min(0), Validators.max(100)]],
    })
  }

  addLegalAddress(): FormGroup {
    return this.fb.group({
      country: ['', [Validators.required]],
      state: ['', [Validators.required]],
      municipality: ['', [Validators.required]],
      colony: ['', [Validators.required]],
      city: ['', [Validators.required]],
      postalCode: ['', [Validators.required, Validators.pattern(/^[0-9]{5}$/)]],
      street: ['', [Validators.required]],
      exteriorNumber: ['', [Validators.pattern(/^[0-9]+[A-Z]?$/)]],
      interiorNumber: ['', [Validators.pattern(/^[0-9]+[A-Z]?$/)]],
    })
  }

  get contacts(): FormArray {
    return this.entity.get('contacts') as FormArray
  }

  addContact() {
    this.contacts.push(this.createContact())
  }

  removeContact(index: number) {
    this.contacts.removeAt(index)
  }

  loadEntityForEdit(customerData: any) {
    // Establecer modo edición ANTES de crear el formulario
    this.isEditMode = true

    // Determinar el tipo de persona
    this.formMethod = customerData.entity === 'Física' ? 'CREATE_PHYSICAL_PERSON' : 'CREATE_MORAL_PERSON'

    // Crear formulario (ahora ya con isEditMode = true)
    this.newEntity()

    // Cargar datos existentes
    this.entity.patchValue(customerData)

    //* Omnicanal: el form usa `channelKey`, pero el cliente trae el canal en `currentChannelKey`.
    //* Pre-seleccionar el canal actual para que el dropdown lo muestre; si el usuario lo cambia,
    //* el backend reasigna (clients.update → channelService.assign). Si lo deja igual, es no-op.
    this.entity.get('channelKey')?.setValue(customerData.currentChannelKey ?? null)

    // Deshabilitar el campo entity en modo edición
    this.entity.get('entity')?.disable()

    //* Los obligados solidarios no entran al formulario: no tienen puesto ni participación y el guardado
    //* genérico los conserva tal cual estaban (plan v4.3 §9.5).
    this.loadExistingContacts(withoutGuarantors(customerData.contacts))

    // Marcar formulario como válido para habilitar el botón guardar
    this.markFormAsValid()
  }

  private markFormAsValid() {
    // Marcar todos los controles como touched para que las validaciones se evalúen correctamente
    this.entity.markAllAsTouched()

    // Actualizar el estado de validez del formulario
    this.entity.updateValueAndValidity()
  }

  private loadExistingContacts(contacts: any[]) {
    // Limpiar contactos existentes
    while (this.contacts.length !== 0) {
      this.contacts.removeAt(0)
    }

    // Agregar contactos existentes
    contacts.forEach((contact) => {
      const contactForm = this.createContact()
      contactForm.patchValue(contact)
      this.contacts.push(contactForm)
    })

    // Asegurar al menos un contacto
    if (this.contacts.length === 0) this.addContact()
  }
}
